import { displayText } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import ThemeContext from '../../../../ThemeContext';
import { createWitnessPrep, getCsrfToken, getCase } from '../../../../services/api';
import api from '../../../../services/api';
import { validateField, validateForm } from '../../../../Utils/SessionsValidationsSchema';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import ClientConfig from '../../../../../config/Clientconfig';
import { URL_SEGMENTS } from '../../../../../config/Routesconfig';

//  shorthand for this session type
const SC = ClientConfig.sessions.types.witness_prep;

const AddWitnessPrep = ({ isEdit = false }) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const params = useParams();
  const navigate = useNavigate();

  // ── Flexible param extraction ───────
  const caseIdStr = params.caseId || params.id;
  const sessionIdStr = params.sessionId;

  const caseId = caseIdStr ? parseInt(caseIdStr, 10) : null;
  const sessionId = sessionIdStr ? parseInt(sessionIdStr, 10) : null;

  const generatePasscode = () => Math.floor(10000 + Math.random() * 90000).toString();

  const [formData, setFormData] = useState({
  witness: '',
  title: '',
  introducing_party: [],
  date: '',
  details: '',
  location: '',
  session_passcode: generatePasscode(),

  schedule_video: false,
  zoom_tech: '',
  registrants: '',
  meeting_duration: '60',
  meeting_time: '',
  meeting_timezone: '',

  zoom_meeting_id: '',
  zoom_join_url: '',
  zoom_start_url: '',
  zoom_password: '',
  zoom_created_at: '',
});

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [combinedUsers, setCombinedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // ── 1. Early URL validation ─────────
  useEffect(() => {
    if (!caseId || isNaN(caseId)) {
      toast.error(displayText('Invalid case ID'));
      navigate(`/${params.username}/dashboard/${URL_SEGMENTS.cases}`);
      return;
    }

    if (isEdit && (!sessionId || isNaN(sessionId))) {
      toast.error(displayText('Invalid witness prep session ID'));
      navigate(`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
    }
  }, [caseId, sessionId, isEdit, navigate, params.username]);

  // ── 2. CSRF + Combined Users (both modes) ──────────────────────────
  useEffect(() => {
    const initializeCsrf = async () => {
      try {
        await getCsrfToken();
      } catch (err) {
        console.error('CSRF init failed:', err);
        toast.error('Failed to initialize security token.');
      }
    };
    initializeCsrf();

    const fetchCombinedUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await api.get('/client-users/combined-for-introducing');
        if (response.data.success) {
          setCombinedUsers(response.data.data || []);
        }
      } catch (err) {
        console.error('Failed to load users:', err);
        toast.error('Failed to load introducing party users');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchCombinedUsers();
  }, []);

  // ── 3. Load case data (both add & edit) ─────────────────────────────
  useEffect(() => {
    if (!caseId || isNaN(caseId)) return;

    const fetchCase = async () => {
      try {
        const res = await getCase(caseId);
        setCaseData(res.data.data);
      } catch (err) {
        console.error('Case load failed:', err);
        toast.error(displayText('Failed to load case details'));
        navigate(`/${params.username}/dashboard/${URL_SEGMENTS.cases}`);
      }
    };
    fetchCase();
  }, [caseId, navigate, params.username]);

  // ── 4. Load existing witness prep data (edit mode only) ────────────
  useEffect(() => {
    if (!isEdit || !sessionId) return;

    const loadWitnessPrep = async () => {
      try {
        setIsLoading(true);
        const res = await api.get(`/witness-preps/${sessionId}`);
        const wp = res.data.data || res.data;

        if (!wp) throw new Error('No witness prep data received');

        if (wp.case_id !== caseId) {
          throw new Error('This witness prep does not belong to the selected case');
        }

        const formattedDate = wp.date ? wp.date.split('T')[0] : '';

        const parties = wp.introducing_party
          ? wp.introducing_party.split(',').map(p => p.trim()).filter(Boolean)
          : [];

        setFormData({
          witness: wp.witness || '',
          title: wp.title || '',
          introducing_party: parties,
          date: formattedDate,
          details: wp.details || '',
          location: wp.location || '',
          session_passcode: wp.session_passcode || generatePasscode(),
          schedule_video: !!wp.schedule_video,
          zoom_tech: wp.zoom_tech || '',
          registrants: wp.registrants || '',
          meeting_time: wp.meeting_time || '',
          meeting_timezone: wp.meeting_timezone || '',
        });

        console.log('Witness Prep loaded successfully:', wp);
      } catch (err) {
        console.error('Failed to load witness prep:', err);
        toast.error(displayText('Failed to load witness prep data: ') + (err.message || 'Unknown error'));
        navigate(`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadWitnessPrep();
  }, [isEdit, sessionId, caseId, navigate, params.username]);

  const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target || {};

  if (name === 'schedule_video') {
    const isEnabled = checked;

    setFormData((prev) => ({
      ...prev,
      schedule_video: isEnabled,
      zoom_tech: isEnabled ? 'zoom' : '',
      registrants: isEnabled ? prev.registrants : '',
      meeting_duration: isEnabled ? (prev.meeting_duration || '60') : '',
      meeting_time: isEnabled ? prev.meeting_time : '',
      meeting_timezone: isEnabled ? prev.meeting_timezone : '',

      zoom_meeting_id: isEnabled ? prev.zoom_meeting_id : '',
      zoom_join_url: isEnabled ? prev.zoom_join_url : '',
      zoom_start_url: isEnabled ? prev.zoom_start_url : '',
      zoom_password: isEnabled ? (prev.session_passcode || '') : '',
      zoom_created_at: isEnabled ? prev.zoom_created_at : '',
    }));

    setErrors((prev) => ({
      ...prev,
      zoom_tech: '',
      registrants: '',
      meeting_duration: '',
      meeting_time: '',
      meeting_timezone: '',
    }));

    return;
  }

  setFormData((prev) => ({
    ...prev,
    [name]: type === 'checkbox' ? checked : value,
  }));
};

  const handleBlur = async (e) => {
    const { name } = e.target;
    await validateField(name, formData, setErrors, 'witness_prep');
  };

  const handleIntroducingPartySelect = (e) => {
    const val = e.target.value;
    if (val && !formData.introducing_party.includes(val)) {
      setFormData((prev) => ({
        ...prev,
        introducing_party: [...prev.introducing_party, val],
      }));
      e.target.value = '';
    }
  };

  const removeIntroducingParty = (party) => {
    setFormData((prev) => ({
      ...prev,
      introducing_party: prev.introducing_party.filter(p => p !== party),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setIsLoading(true);

    if (formData.introducing_party.length === 0) {
      toast.error('Please select at least one introducing party');
      setIsLoading(false);
      setIsSubmitting(false);
      return;
    }

    try {
      const validationData = {
        ...formData,
        introducing_party: formData.introducing_party
          .map(p => p.trim())
          .filter(Boolean)
          .join(', ')
      };

      const isValid = await validateForm(validationData, setErrors, 'witness_prep');
      if (!isValid) {
        toast.error('Please correct the highlighted fields.');
        return;
      }

      const payload = {
  case_id: caseId,
  witness: formData.witness?.trim() || '',
  title: formData.title?.trim() || null,
  introducing_party: validationData.introducing_party,
  date: formData.date || null,
  details: formData.details?.trim() || null,
  location: formData.location?.trim() || '',
  session_passcode: formData.session_passcode || '',

  schedule_video: !!formData.schedule_video,
  zoom_tech: formData.schedule_video ? 'zoom' : null,
  registrants: formData.schedule_video ? (formData.registrants?.trim() || null) : null,
  meeting_duration: formData.schedule_video ? (formData.meeting_duration || null) : null,
  meeting_time: formData.schedule_video ? formData.meeting_time : null,
  meeting_timezone: formData.schedule_video ? formData.meeting_timezone : null,
};

      console.log('Sending witness prep payload:', payload);

      let response;
      if (isEdit) {
        response = await api.put(`/witness-preps/${sessionId}`, payload);
      } else {
        response = await createWitnessPrep(payload);
      }

      if ([200, 201, 204].includes(response.status)) {
        //  SC.label
        toast.success(`${SC.label} ${isEdit ? 'updated' : 'created'} successfully!`);
        navigate(`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
      } else {
        throw new Error(`Unexpected status: ${response.status}`);
      }
    } catch (error) {
      console.error('Witness Prep save failed:', error);

      //  SC.label
      let msg = `Failed to save ${SC.label}`;

      if (error.response?.data) {
        if (error.response.data.errors) {
          const field = Object.keys(error.response.data.errors)[0];
          msg = `${field}: ${error.response.data.errors[field].join(', ')}`;
          setErrors(error.response.data.errors);
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        }
      }

      toast.error(msg);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    navigate(`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
  };

  // ── JSX (same modern style as others) ──────────────────────────────
  return (
    <>
      {/* Breadcrumb */}
      <div style={{ padding: '0rem 1.0rem 1.0rem 0rem', margin: 0 }}>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0" style={{ fontSize: '10px', padding: 0, background: 'transparent' }}>
            <li className="breadcrumb-item">
              <Link
                to={`/${params.username}/dashboard/${URL_SEGMENTS.cases}`}
                style={{ color: theme.colors.primary, textDecoration: 'none', fontWeight: 'bold' }}
              >
                {/*  ClientConfig */}
                {ClientConfig.cases.managementLabel}
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link
                to={`/${params.username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`}
                style={{ color: theme.colors.textPrimary, textDecoration: 'none', fontWeight: 'bold' }}
              >
                {caseData?.case_name || 'Loading...'}
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: theme.colors.textPrimary, fontWeight: 'bold' }}>
              {/*  SC.label */}
              {isEdit ? 'Edit' : 'Add'}: {SC.label}
            </li>
          </ol>
        </nav>
      </div>

      {/* Main Container */}
      <div
        className="container-fluid py-4"
        style={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          fontFamily: theme?.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif',
          fontSize: 'clamp(10px, 2.5vw, 11px)',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${theme.colors.borderColor || '#ddd'}`,
          margin: '0 1.0rem 1.0rem 0',
        }}
      >
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastStyle={{
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            fontSize: '10px',
            fontWeight: '500',
          }}
        />

        <div style={{ borderRadius: '10px', border: `1px solid ${theme.colors.borderColor}`, overflow: 'hidden', backgroundColor: theme.colors.bgPrimary }}>
          <div
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              gap: '12px',
            }}
          >
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              {/*  SC.label */}
              {isEdit ? 'Edit' : 'Add'}: {SC.label}
            </h5>
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              {/*  SC.relatedToLabel + ClientConfig.cases.label */}
              {SC.relatedToLabel} {ClientConfig.cases.label}: {caseData?.case_name || 'Loading...'}
            </h5>
          </div>

          <div style={{ padding: '16px' }}>
            <form id="addWitnessPrepForm" onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: window.innerWidth < 600 ? 'column' : 'row', gap: '24px', alignItems: 'flex-start' }}>
                {/* LEFT COLUMN */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Witness */}
                  <div>
                    <label htmlFor="witness" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      {/*  SC.fieldLabel */}
                      {SC.fieldLabel}*
                    </label>
                    <input
                      type="text"
                      id="witness"
                      name="witness"
                      value={formData.witness}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter  name"
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.witness ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.witness ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666', outline: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', transition: 'border-color 0.3s ease', opacity: isSubmitting ? 0.6 : 1 }}
                    />
                    {errors.witness && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.witness}</div>}
                  </div>

                  {/* Title (optional) */}
                  <div>
                    <label htmlFor="title" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      Title / Affiliation
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter title or affiliation (optional)"
                      disabled={isSubmitting}
                      className="form-control"
                      style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666' }}
                    />
                  </div>

                  {/* Introducing Party */}
                  <div>
                    <label htmlFor="introducingParty" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      Introducing Party*
                    </label>
                    <select
                      id="introducingParty"
                      onChange={handleIntroducingPartySelect}
                      disabled={isSubmitting || loadingUsers}
                      className={`form-control ${errors.introducing_party ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.introducing_party ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666' }}
                    >
                      <option value="">{loadingUsers ? 'Loading...' : 'Select to add...'}</option>
                      {combinedUsers.map(user => (
                        <option key={`${user.type}-${user.id}`} value={user.display_name}>
                          {user.display_name}
                        </option>
                      ))}
                    </select>

                    {formData.introducing_party.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {formData.introducing_party.map((party, i) => (
                          <div key={i} style={{ backgroundColor: theme.colors.primary, color: '#fff', padding: '4px 8px', borderRadius: '12px', fontSize: '9px', display: 'inline-flex', alignItems: 'center' }}>
                            {party}
                            <button type="button" onClick={() => removeIntroducingParty(party)} style={{ background: 'none', border: 'none', color: '#fff', marginLeft: '6px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {errors.introducing_party && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.introducing_party}</div>}
                  </div>

                  {/* Date */}
                  <div>
                    <label htmlFor="date" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      Date*
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.date ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666' }}
                    />
                    {errors.date && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.date}</div>}
                  </div>

                  {/* Details */}
                  <div>
                    <label htmlFor="details" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      Details
                    </label>
                    <textarea
                      id="details"
                      name="details"
                      value={formData.details}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      rows="2"
                      disabled={isSubmitting}
                      className={`form-control ${errors.details ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.details ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666', resize: 'vertical' }}
                    />
                    {errors.details && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.details}</div>}
                  </div>

                  {/* Location */}
                  <div>
                    <label htmlFor="location" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      {/*  SC.locationLabel */}
                      {SC.locationLabel}
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                      style={{ width: '100%', border: `1px solid ${errors.location ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: theme.colors.bgPrimary, color: '#666' }}
                    />
                    {errors.location && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.location}</div>}
                  </div>

                  {/* Session Passcode */}
                  <div>
                    <label htmlFor="sessionPasscode" style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>{displayText(" Session Passcode* (Auto-Generated) ")}</label>
                    <input
                      type="text"
                      id="sessionPasscode"
                      name="session_passcode"
                      value={formData.session_passcode}
                      readOnly
                      disabled={isSubmitting}
                      style={{ width: '100%', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px', fontSize: '10px', fontWeight: '100', backgroundColor: '#f0f0f0', color: '#333', cursor: 'not-allowed' }}
                    />
                  </div>
                </div>

                {/* Divider */}
                <div
                  style={{
                    width: window.innerWidth < 600 ? '100%' : '1px',
                    height: window.innerWidth < 600 ? '1px' : 'auto',
                    backgroundColor: 'transparent',
                    backgroundImage: window.innerWidth < 600
                      ? `repeating-linear-gradient(to right, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`
                      : `repeating-linear-gradient(to bottom, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`,
                    margin: window.innerWidth < 600 ? '12px 0' : '0 16px',
                    minHeight: window.innerWidth >= 600 ? '400px' : '1px',
                  }}
                />

                {/* RIGHT COLUMN - Video Conference */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label
                      htmlFor="scheduleVideo"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        margin: 0,
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                    >
                      Schedule a Video Conference?
                    </label>
                    <div
                      style={{
                        position: 'relative',
                        width: '50px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: formData.schedule_video ? theme.colors.success : '#ccc',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                      onClick={() => {
  if (!isSubmitting) {
    const nextValue = !formData.schedule_video;

    setFormData((prev) => ({
      ...prev,
      schedule_video: nextValue,
      zoom_tech: nextValue ? 'zoom' : '',
      registrants: nextValue ? prev.registrants : '',
      meeting_duration: nextValue ? (prev.meeting_duration || '60') : '',
      meeting_time: nextValue ? prev.meeting_time : '',
      meeting_timezone: nextValue ? prev.meeting_timezone : '',

      // Zoom meeting fields
      zoom_meeting_id: nextValue ? prev.zoom_meeting_id : '',
      zoom_join_url: nextValue ? prev.zoom_join_url : '',
      zoom_start_url: nextValue ? prev.zoom_start_url : '',
      zoom_password: nextValue ? (prev.session_passcode || '') : '',
      zoom_created_at: nextValue ? prev.zoom_created_at : '',
    }));
  }
}}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: formData.schedule_video ? '28px' : '2px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: theme.colors.textWhite,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          transition: 'left 0.3s ease',
                        }}
                      />
                      <input
  type="checkbox"
  id="scheduleVideo"
  name="schedule_video"
  checked={formData.schedule_video}
  onChange={handleInputChange}
  disabled={isSubmitting}
  style={{ display: 'none' }}
/>
                    </div>
                  </div>

                  {formData.schedule_video && (
                    <>
                      <div>
  <label
    htmlFor="zoomMeetingId"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Zoom Meeting ID
  </label>
  <input
    type="text"
    id="zoomMeetingId"
    name="zoom_meeting_id"
    value={formData.zoom_meeting_id}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be generated after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label
    htmlFor="zoomJoinUrl"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Zoom Join URL
  </label>
  <input
    type="text"
    id="zoomJoinUrl"
    name="zoom_join_url"
    value={formData.zoom_join_url}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be generated after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label
    htmlFor="zoomStartUrl"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Zoom Start URL
  </label>
  <input
    type="text"
    id="zoomStartUrl"
    name="zoom_start_url"
    value={formData.zoom_start_url}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be generated after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label
    htmlFor="zoomPassword"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Zoom Password
  </label>
  <input
    type="text"
    id="zoomPassword"
    name="zoom_password"
    value={formData.zoom_password}
    readOnly
    disabled
    className="form-control"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label
    htmlFor="zoomCreatedAt"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Zoom Created At
  </label>
  <input
    type="text"
    id="zoomCreatedAt"
    name="zoom_created_at"
    value={formData.zoom_created_at}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be filled after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>
<div>
  <label
    htmlFor="meetingDuration"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Meeting Duration (mins)
  </label>
  <input
    type="number"
    id="meetingDuration"
    name="meeting_duration"
    value={formData.meeting_duration}
    onChange={handleInputChange}
    disabled={isSubmitting}
    className="form-control"
    min="15"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: theme.colors.bgPrimary,
      color: '#666',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
    }}
  />
</div>

<div>
  <label
    htmlFor="meetingTime"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Meeting Time
  </label>
  <input
    type="time"
    id="meetingTime"
    name="meeting_time"
    value={formData.meeting_time}
    onChange={handleInputChange}
    disabled={isSubmitting}
    className="form-control"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: theme.colors.bgPrimary,
      color: '#666',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
    }}
  />
</div>

<div>
  <label
    htmlFor="meetingTimezone"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Meeting Timezone
  </label>
  <select
    id="meetingTimezone"
    name="meeting_timezone"
    value={formData.meeting_timezone}
    onChange={handleInputChange}
    disabled={isSubmitting}
    className="form-control"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: theme.colors.bgPrimary,
      color: '#666',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
    }}
  >
    <option value="">Select timezone</option>
    <option value="Asia/Kolkata">Asia/Kolkata</option>
    <option value="UTC">UTC</option>
    <option value="America/New_York">America/New_York</option>
    <option value="America/Los_Angeles">America/Los_Angeles</option>
  </select>
</div>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            padding: '12px',
            backgroundColor: theme.colors.bgPrimary,
          }}
        >
          <div style={{ display: 'flex', gap: '8px', flexDirection: window.innerWidth < 600 ? 'column' : 'row', width: window.innerWidth < 600 ? '100%' : 'auto' }}>
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              style={{
                background: theme.gradients.secondary,
                color: theme.colors.textWhite,
                fontSize: '9px',
                fontWeight: '600',
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                minWidth: '120px',
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="addWitnessPrepForm"
              disabled={isLoading}
              style={{
                background: theme.gradients.primary,
                color: theme.colors.textWhite,
                fontSize: '9px',
                fontWeight: '600',
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                minWidth: '120px',
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Saving...' : isEdit ? 'Update' : 'Save'}
            </button>
          </div>

          {isLoading && (
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}>
              <div style={{ width: '40px', height: '40px', border: `4px solid ${theme.colors.primary}`, borderTop: '4px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
          )}
        </div>

        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </>
  );
};

export default AddWitnessPrep;